import { useEffect, useCallback } from "react";
import { performanceLogger } from "../utils/logger";
/**
 * Custom hook for monitoring web performance metrics
 */
export var usePerformanceMonitoring = function (options) {
    if (options === void 0) { options = {}; }
    var _a = options.reportToConsole, reportToConsole = _a === void 0 ? true : _a, _b = options.reportToAnalytics, reportToAnalytics = _b === void 0 ? false : _b, _c = options.thresholds, thresholds = _c === void 0 ? {
        fcp: 1800,
        lcp: 2500,
        fid: 100,
        cls: 0.1, // 0.1
    } : _c;
    var reportMetric = useCallback(function (name, value, threshold) {
        var isGood = threshold ? value <= threshold : true;
        var status = isGood ? "✅" : "⚠️";
        if (reportToConsole) {
            performanceLogger.log("".concat(status, " ").concat(name, ": ").concat(value.toFixed(2)).concat(name.includes("Delay") || name.includes("Paint") ? "ms" : ""));
        }
        if (reportToAnalytics && window.gtag) {
            window.gtag("event", "web_vitals", {
                event_category: "Performance",
                event_label: name,
                value: Math.round(value),
                custom_map: { metric_threshold: threshold },
            });
        }
    }, [reportToConsole, reportToAnalytics]);
    var measureWebVitals = useCallback(function () {
        // First Contentful Paint
        if ("PerformanceObserver" in window) {
            try {
                var observer = new PerformanceObserver(function (list) {
                    for (var _i = 0, _a = list.getEntries(); _i < _a.length; _i++) {
                        var entry = _a[_i];
                        if (entry.name === "first-contentful-paint") {
                            reportMetric("First Contentful Paint", entry.startTime, thresholds.fcp);
                        }
                    }
                });
                observer.observe({ entryTypes: ["paint"] });
            }
            catch (e) {
                performanceLogger.warn("Performance Observer not supported for paint metrics");
            }
            // Largest Contentful Paint
            try {
                var lcpObserver = new PerformanceObserver(function (list) {
                    var entries = list.getEntries();
                    var lastEntry = entries[entries.length - 1];
                    reportMetric("Largest Contentful Paint", lastEntry.startTime, thresholds.lcp);
                });
                lcpObserver.observe({ entryTypes: ["largest-contentful-paint"] });
            }
            catch (e) {
                performanceLogger.warn("Performance Observer not supported for LCP metrics");
            }
            // First Input Delay
            try {
                var fidObserver = new PerformanceObserver(function (list) {
                    for (var _i = 0, _a = list.getEntries(); _i < _a.length; _i++) {
                        var entry = _a[_i];
                        var fidEntry = entry;
                        reportMetric("First Input Delay", fidEntry.processingStart - fidEntry.startTime, thresholds.fid);
                    }
                });
                fidObserver.observe({ entryTypes: ["first-input"] });
            }
            catch (e) {
                performanceLogger.warn("Performance Observer not supported for FID metrics");
            }
            // Cumulative Layout Shift
            try {
                var clsValue_1 = 0;
                var clsObserver = new PerformanceObserver(function (list) {
                    for (var _i = 0, _a = list.getEntries(); _i < _a.length; _i++) {
                        var entry = _a[_i];
                        var layoutShift = entry;
                        if (!layoutShift.hadRecentInput) {
                            clsValue_1 += layoutShift.value;
                        }
                    }
                    reportMetric("Cumulative Layout Shift", clsValue_1, thresholds.cls);
                });
                clsObserver.observe({ entryTypes: ["layout-shift"] });
            }
            catch (e) {
                performanceLogger.warn("Performance Observer not supported for CLS metrics");
            }
        }
        // Navigation Timing API fallback
        if (performance.timing) {
            var timing = performance.timing;
            var navigationStart = timing.navigationStart;
            // DOM Content Loaded
            var domContentLoaded = timing.domContentLoadedEventEnd - navigationStart;
            reportMetric("DOM Content Loaded", domContentLoaded, 1500);
            // Load Event
            var loadEvent = timing.loadEventEnd - navigationStart;
            reportMetric("Load Event", loadEvent, 3000);
            // Time to Interactive (approximation)
            var timeToInteractive = timing.domInteractive - navigationStart;
            reportMetric("Time to Interactive", timeToInteractive, 3800);
        }
    }, [reportMetric, thresholds]);
    var measureResourceTiming = useCallback(function () {
        var resources = performance.getEntriesByType("resource");
        var jsResources = resources.filter(function (resource) {
            return resource.name.includes(".js") && !resource.name.includes("chunk");
        });
        var totalJSSize = 0;
        var totalJSLoadTime = 0;
        jsResources.forEach(function (resource) {
            if (resource.transferSize) {
                totalJSSize += resource.transferSize;
            }
            totalJSLoadTime += resource.responseEnd - resource.fetchStart;
        });
        if (reportToConsole) {
            performanceLogger.log("📊 Resource Performance:");
            performanceLogger.log("  Total JS Size: ".concat((totalJSSize / 1024).toFixed(2), " KB"));
            performanceLogger.log("  Total JS Load Time: ".concat(totalJSLoadTime.toFixed(2), "ms"));
            performanceLogger.log("  JS Resources: ".concat(jsResources.length));
        }
    }, [reportToConsole]);
    useEffect(function () {
        // Measure Web Vitals
        measureWebVitals();
        // Measure resource timing after load
        if (document.readyState === "complete") {
            measureResourceTiming();
        }
        else {
            window.addEventListener("load", measureResourceTiming, { once: true });
        }
        return function () {
            window.removeEventListener("load", measureResourceTiming);
        };
    }, [measureWebVitals, measureResourceTiming]);
};
/**
 * Performance monitoring component
 */
export var PerformanceMonitor = function (props) {
    usePerformanceMonitoring(props);
    return null;
};
/**
 * Measure component render time
 */
export var useRenderTime = function (componentName) {
    useEffect(function () {
        var startTime = performance.now();
        return function () {
            var endTime = performance.now();
            var renderTime = endTime - startTime;
            performanceLogger.log("\uD83C\uDFAD ".concat(componentName, " render time: ").concat(renderTime.toFixed(2), "ms"));
        };
    });
};
/**
 * Memory usage monitoring
 */
export var useMemoryMonitoring = function () {
    useEffect(function () {
        if ("memory" in performance) {
            var memory = performance.memory;
            performanceLogger.log("💾 Memory Usage:", {
                used: "".concat((memory.usedJSHeapSize / 1048576).toFixed(2), " MB"),
                total: "".concat((memory.totalJSHeapSize / 1048576).toFixed(2), " MB"),
                limit: "".concat((memory.jsHeapSizeLimit / 1048576).toFixed(2), " MB"),
            });
        }
    }, []);
};
export default {
    usePerformanceMonitoring: usePerformanceMonitoring,
    PerformanceMonitor: PerformanceMonitor,
    useRenderTime: useRenderTime,
    useMemoryMonitoring: useMemoryMonitoring,
};
